import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  FileText,
  History,
  Menu,
  X,
  LogOut,
  UserCheck,
  Upload,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';

export const UserLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('ayur_user_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('ayur_user_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      navigate('/login');
    }
  };

  const menuItems = [
    { icon: ClipboardList, label: 'My Work Queue', shortLabel: 'Work Queue', path: '/user/dashboard' },
    { icon: Upload, label: 'Upload Prescription', shortLabel: 'Upload', path: '/user/prescriptions/upload' },
    { icon: FileText, label: 'Assigned Prescriptions', shortLabel: 'Prescriptions', path: '/user/prescriptions' },
    { icon: History, label: 'Completed History', shortLabel: 'History', path: '/user/history' },
  ];

  const isItemActive = (path) => {
    if (path === '/user/prescriptions') {
      return location.pathname === '/user/prescriptions' || (/^\/user\/prescriptions\/\d+/.test(location.pathname));
    }
    return location.pathname === path || (path !== '/user' && location.pathname.startsWith(path));
  };

  return (
    <div className="h-screen h-[100dvh] min-h-[100dvh] bg-background flex flex-col">
      {/* Top Navigation Bar */}
      <header className="bg-card border-b border-border sticky top-0 z-40 safe-top">
        <div className="flex items-center justify-between h-16 px-4 md:px-6">
          <div className="flex items-center gap-2 md:gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Link to="/user/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Sanjivani Ayurvedic Clinic" className="h-11 w-auto object-contain" />
              <Badge variant="outline" className="hidden sm:inline-flex bg-primary/5 text-primary border-primary/20 text-xs">
                Operational Workspace
              </Badge>
            </Link>
          </div>

          <div className="flex items-center gap-3 md:gap-4">
            <NotificationDropdown />
            <div className="hidden md:flex flex-col items-end">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{user?.name}</span>
                <Badge variant="secondary" className="text-xs font-normal">
                  Staff
                </Badge>
              </div>
              <span className="text-xs text-muted-foreground">{user?.username ? `@${user.username}` : user?.email}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} title="Log out" aria-label="Log out">
              <LogOut className="h-5 w-5 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed md:sticky top-16 left-0 z-30 bg-card border-r border-border transition-all duration-300 ease-in-out overflow-y-auto flex flex-col",
            isCollapsed ? "w-64 md:w-16" : "w-64",
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}
        >
          {/* Sidebar inner toggle header */}
          <div className={cn("border-b border-border/50 py-3", isCollapsed ? "px-2 flex justify-center" : "px-4 flex items-center justify-between")}>
            {!isCollapsed && (
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider truncate">
                <UserCheck className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Fulfillment</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:flex h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={toggleSidebarCollapse}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="user-sidebar-inner-toggle"
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>

          <nav className={cn("space-y-1 flex-1", isCollapsed ? "p-2" : "p-4")}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = isItemActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  title={item.label}
                  className={cn(
                    "flex items-center rounded-lg transition-colors text-sm font-medium",
                    isCollapsed
                      ? "md:justify-center md:px-2 md:py-2.5 px-4 py-2.5 gap-3"
                      : "gap-3 px-4 py-2.5",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "hover:bg-muted text-foreground"
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className={cn("truncate", isCollapsed && "md:hidden")}>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-foreground/20 z-20 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto max-w-7xl mx-auto w-full user-content-bottom-safe md:pb-6 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* ── Mobile Bottom Navigation (< md) ── */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border md:hidden mobile-nav-safe"
        aria-label="Mobile navigation"
      >
        <div className="flex items-center h-14">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isItemActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center gap-0.5 py-1 px-1 h-full min-h-[56px]",
                  "transition-colors",
                  active
                    ? "text-primary font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                )}
                aria-current={active ? 'page' : undefined}
                aria-label={item.label}
              >
                <div className={cn(
                  "p-1 rounded-lg transition-all",
                  active && "bg-primary/10"
                )}>
                  <Icon className={cn("h-5 w-5", active && "stroke-[2.5]")} />
                </div>
                <span className="text-[10px] sm:text-[11px] font-medium tracking-tight leading-none">
                  {item.shortLabel}
                </span>
              </Link>
            );
          })}
          {/* Logout in bottom nav */}
          <button
            onClick={handleLogout}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1 px-1 h-full min-h-[56px] text-muted-foreground hover:text-destructive transition-colors"
            aria-label="Sign out"
          >
            <div className="p-1 rounded-lg">
              <LogOut className="h-5 w-5" />
            </div>
            <span className="text-[10px] sm:text-[11px] font-medium tracking-tight leading-none">Logout</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default UserLayout;
